import { MousePointerClick, EyeOff, Layers, Workflow } from "lucide-react";

const problems = [
  {
    icon: MousePointerClick,
    title: "Too many clicks, too little clarity",
    body:
      "Modern websites are crowded with menus, pop-ups, and interactions that demand precision mouse control and sharp vision to navigate.",
  },
  {
    icon: EyeOff,
    title: "Visibility is an afterthought",
    body:
      "Low-contrast text, tiny buttons, and inaccessible flows exclude people with visual impairments from everyday digital tasks.",
  },
  {
    icon: Layers,
    title: "Cognitive overload is the default",
    body:
      "Endless filters, nested categories, and decorative content force users to hold too much in working memory just to complete a goal.",
  },
  {
    icon: Workflow,
    title: "Traditional interfaces are limiting",
    body:
      "Point-and-click was designed for a different era. It assumes one kind of user, one way of browsing, one type of device.",
  },
];

const Problem = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-24 items-start">
          {/* left — label + heading */}
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
              <span className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-400">
                The Problem
              </span>
            </div>
            <h2 className="mt-6 font-display font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl text-balance">
              The web wasn't built for how real people{" "}
              <span className="text-zinc-500">see, move, and think.</span>
            </h2>
            <p className="mt-6 font-body text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              For millions of users with visibility and accessibility needs, everyday tasks — booking a
              ticket, filling a form, finding a product — turn into exhausting puzzles of tiny targets and
              cluttered screens.
            </p>
          </div>

          {/* right — grid of problems */}
          <div className="grid sm:grid-cols-2 border-l border-t border-white/[0.06]">
            {problems.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  className="group relative border-r border-b border-white/[0.06] p-7 md:p-8 transition-all hover:bg-white/[0.02]"
                  data-testid={`problem-card-${i}`}
                >
                  <Icon className="h-6 w-6 text-zinc-300 group-hover:text-indigo-300 transition-colors" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display font-medium text-white text-lg md:text-xl tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 font-body text-[15px] leading-relaxed text-zinc-400">
                    {p.body}
                  </p>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-indigo-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Problem;
